import React from "react";
import { Bookmark, Globe, Mic, TrendingUp } from "lucide-react";
import { collagePages } from "../mock";
import { useSiteData } from "../context/SiteDataContext";
import { getSection, imageStyle, renderMultilineText, sectionStyle } from "../lib/sectionStyles";
import SafeImage from "../components/SafeImage";

const icons = {
  VIRAL: TrendingUp,
  PANEL: Mic,
  REFERENCED: Bookmark,
  GLOBAL: Globe,
};

const Highlights = () => {
  const { highlights, siteSections } = useSiteData();
  const section = getSection(siteSections, "home", "home.public-proof", {
    eyebrow: "Section 04 - Highlights",
    title: "THEN\nTHE PIECE\nBLEW UP.",
    body: "Receipts from the byline - viral pieces, panels, and citations.",
    caption: "dubai panel + dazed mena spread",
    imageUrl: collagePages.dazed,
    imageAlt: "Dazed coverage and Dubai panel",
    imagePosition: "center",
    imageFit: "cover",
  });

  if (!section) return null;
  const titleLines = String(section.title || "").split(/\n+/).filter(Boolean);

  return (
    <section className="max-w-[1400px] mx-auto px-5 sm:px-8 py-20" style={sectionStyle(section)}>
      <div className="grid grid-cols-12 gap-8 md:gap-12 items-start">
        <div className="col-span-12 md:col-span-5">
          <span className="font-mono text-[11px] tracking-[0.3em] uppercase text-[var(--ink-soft)]">
            {section.eyebrow}
          </span>
          <h2 className="font-masthead leading-[0.88] text-7xl md:text-8xl mt-3">
            {titleLines[0] || "THEN"}
            <br />
            <span className="text-[var(--red)] relative inline-block">
              {titleLines[1] || "THE PIECE"}
              <span className="absolute left-0 right-0 -bottom-0.5 h-2 bg-[var(--pink)]" aria-hidden="true" />
            </span>
            <br />
            {titleLines.slice(2).join(" ") || "BLEW UP."}
          </h2>
          <p className="mt-6 font-editorial italic text-xl text-[var(--ink-soft)]">
            {renderMultilineText(section.body)}
          </p>

          <div className="mt-8 relative">
            <span className="tape -top-3 left-12 -rotate-3" />
            <div className="border-4 border-[var(--ink)] overflow-hidden bg-[var(--ink)]">
              <SafeImage
                src={section.imageUrl || collagePages.dazed}
                fallbackSrc={collagePages.dazed}
                alt={section.imageAlt || "Dazed coverage and Dubai panel"}
                loading="lazy"
                width="940"
                height="705"
                className="w-full h-auto object-cover"
                style={imageStyle(section)}
              />
            </div>
            <p className="mt-3 font-hand text-2xl text-[var(--red-deep)] -rotate-1">
              -&gt; {section.caption || "dubai panel + dazed mena spread"}
            </p>
          </div>
        </div>

        <div className="col-span-12 md:col-span-7 grid grid-cols-1 sm:grid-cols-2 gap-5">
          {highlights.map((h) => {
            const Icon = icons[h.stamp] || TrendingUp;
            return (
              <article
                key={h.id}
                className="relative group border-2 border-[var(--ink)] bg-[var(--paper)] p-6 hover:bg-[var(--pink-soft)] transition-colors shadow-[4px_4px_0_var(--ink)]"
              >
                <div className="flex items-center justify-between mb-4">
                  <span className="stamp">{h.stamp}</span>
                  <Icon size={22} className="text-[var(--red)]" />
                </div>
                <h3 className="font-editorial text-2xl md:text-3xl leading-tight">
                  {h.title}
                </h3>
                <p className="mt-3 text-sm text-[var(--ink-soft)] leading-relaxed">
                  {renderMultilineText(h.body)}
                </p>
              </article>
            );
          })}
        </div>
      </div>
    </section>
  );
};

export default Highlights;
